const LISTINGS_LOAD_MODE = "button";
const LISTINGS_MODE_BUTTON = "button";
const LISTINGS_MODE_SCROLL = "scroll";
const LISTINGS_DEBUG = true;

const initInfiniteListings = () => {
	const listingsRoot = document.querySelector("[data-infinite-listings]");

	if (!listingsRoot || !window.WP_VARS || !window.WP_VARS.ajaxUrl) {
		return;
	}

	const sentinel = listingsRoot.querySelector(".jb-listings__sentinel");
	const actionsEl = listingsRoot.querySelector(".jb-listings__actions");
	const loadMoreButton = listingsRoot.querySelector("[data-load-more-button]");
	const endMessageEl = listingsRoot.querySelector("[data-end-message]");

	if (!sentinel || !actionsEl) {
		return;
	}

	const state = {
		isLoading: false,
		hasMore: listingsRoot.dataset.hasMore === "1",
		currentCategoryId: Number.parseInt(
			listingsRoot.dataset.currentCategoryId || "0",
			10,
		),
		cursorCategoryIndex: Number.parseInt(
			listingsRoot.dataset.cursorCategoryIndex || "0",
			10,
		),
		cursorJobOffset: Number.parseInt(
			listingsRoot.dataset.cursorJobOffset || "0",
			10,
		),
	};

	let requestCount = 0;

	const activeMode =
		LISTINGS_LOAD_MODE === LISTINGS_MODE_SCROLL ||
		LISTINGS_LOAD_MODE === LISTINGS_MODE_BUTTON
			? LISTINGS_LOAD_MODE
			: LISTINGS_MODE_BUTTON;

	const logDebug = (message, details = null) => {
		if (!LISTINGS_DEBUG) {
			return;
		}

		if (details) {
			console.log(`[Listings] ${message}`, details);
			return;
		}

		console.log(`[Listings] ${message}`);
	};

	const normalizeHasMore = (value) => {
		if (typeof value === "boolean") {
			return value;
		}

		if (typeof value === "number") {
			return value === 1;
		}

		if (typeof value === "string") {
			const normalized = value.trim().toLowerCase();
			return normalized === "1" || normalized === "true" || normalized === "yes";
		}

		return false;
	};

	const updateLoadMoreUi = () => {
		const hasItems = listingsRoot.querySelectorAll(".jb-listings__list-item").length > 0;
		const shouldShowEndMessage = !state.hasMore && hasItems;

		if (endMessageEl) {
			endMessageEl.hidden = !shouldShowEndMessage;
			logDebug(shouldShowEndMessage ? "showing end message" : "hiding end message", {
				hasMore: state.hasMore,
				hasItems,
			});
		}

		if (!loadMoreButton) {
			logDebug("load more button not found in DOM");
			return;
		}

		const shouldShowButton = activeMode === LISTINGS_MODE_BUTTON && state.hasMore;

		loadMoreButton.hidden = !shouldShowButton;
		loadMoreButton.style.display = shouldShowButton ? "" : "none";
		loadMoreButton.disabled = state.isLoading;
		loadMoreButton.textContent = state.isLoading ? "Loading..." : "Load More";

		if (shouldShowButton) {
			logDebug("showing button", {
				isLoading: state.isLoading,
				hasMore: state.hasMore,
				mode: activeMode,
			});
		} else {
			logDebug("hiding button", {
				isLoading: state.isLoading,
				hasMore: state.hasMore,
				mode: activeMode,
			});
		}
	};

	const setCursor = (cursor) => {
		if (!cursor || typeof cursor !== "object") {
			return;
		}

		const categoryIndex = Number.parseInt(cursor.category_index, 10);
		const jobOffset = Number.parseInt(cursor.job_offset, 10);

		if (Number.isFinite(categoryIndex)) {
			state.cursorCategoryIndex = categoryIndex;
			listingsRoot.dataset.cursorCategoryIndex = String(categoryIndex);
		}

		if (Number.isFinite(jobOffset)) {
			state.cursorJobOffset = jobOffset;
			listingsRoot.dataset.cursorJobOffset = String(jobOffset);
		}
	};

	const appendHtml = (html) => {
		if (!html) {
			logDebug("append skipped: empty html");
			return;
		}

		const parserNode = document.createElement("div");
		parserNode.innerHTML = html;

		const incomingGroups = parserNode.querySelectorAll(".jb-listings__group");
		const incomingItems = parserNode.querySelectorAll(".jb-listings__list-item");

		logDebug("append payload parsed", {
			incomingGroups: incomingGroups.length,
			incomingItems: incomingItems.length,
			htmlLength: html.length,
		});

		if (!incomingGroups.length) {
			actionsEl.insertAdjacentHTML("beforebegin", html);
			return;
		}

		incomingGroups.forEach((incomingGroup) => {
			const categoryId = incomingGroup.dataset.categoryId || "";

			if (!categoryId) {
				actionsEl.insertAdjacentElement("beforebegin", incomingGroup);
				return;
			}

			const existingGroup = listingsRoot.querySelector(
				`.jb-listings__group[data-category-id="${categoryId}"]`,
			);

			if (!existingGroup) {
				actionsEl.insertAdjacentElement("beforebegin", incomingGroup);
				return;
			}

			const existingList = existingGroup.querySelector(".jb-listings__list");
			const incomingList = incomingGroup.querySelector(".jb-listings__list");

			if (!existingList || !incomingList) {
				return;
			}

			const incomingItems = incomingList.querySelectorAll(".jb-listings__list-item");

			incomingItems.forEach((incomingItem) => {
				existingList.appendChild(incomingItem);
			});
		});
	};

	const fetchNextPage = async () => {
		if (state.isLoading || !state.hasMore) {
			logDebug("fetch skipped", {
				isLoading: state.isLoading,
				hasMore: state.hasMore,
			});
			return;
		}

		requestCount += 1;
		const requestId = requestCount;
		const requestedCursor = {
			categoryIndex: state.cursorCategoryIndex,
			jobOffset: state.cursorJobOffset,
		};

		logDebug(`request #${requestId} start`, {
			mode: activeMode,
			requestedCursor,
			currentCategoryId: state.currentCategoryId,
		});

		state.isLoading = true;
		updateLoadMoreUi();

		try {
			const body = new URLSearchParams({
				action: "fpjb_get_listings_page",
				nonce: window.WP_VARS.listingsNonce || "",
				current_category_id: String(state.currentCategoryId),
				cursor_category_index: String(state.cursorCategoryIndex),
				cursor_job_offset: String(state.cursorJobOffset),
			});

			const response = await fetch(window.WP_VARS.ajaxUrl, {
				method: "POST",
				credentials: "same-origin",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
				},
				body: body.toString(),
			});

			if (!response.ok) {
				throw new Error("Request failed");
			}

			const payload = await response.json();

			if (!payload || payload.success !== true || !payload.data) {
				throw new Error("Invalid response");
			}

			appendHtml(payload.data.html || "");
			setCursor(payload.data.cursor || null);

			const rawHasMore = payload.data.has_more;
			state.hasMore = normalizeHasMore(rawHasMore);
			listingsRoot.dataset.hasMore = state.hasMore ? "1" : "0";

			logDebug(`request #${requestId} summary`, {
				success: payload.success === true,
				responseOk: response.ok,
				rawHasMore,
				normalizedHasMore: state.hasMore,
				nextCursor: {
					categoryIndex: state.cursorCategoryIndex,
					jobOffset: state.cursorJobOffset,
				},
				returnedHtmlLength: (payload.data.html || "").length,
				returnedGroups: ((payload.data.html || "").match(/jb-listings__group/g) || []).length,
				returnedItems: ((payload.data.html || "").match(/jb-listings__list-item/g) || []).length,
			});

		} catch (error) {
			state.hasMore = false;
			listingsRoot.dataset.hasMore = "0";
			logDebug(`request #${requestId} failed`, {
				error: error instanceof Error ? error.message : String(error),
				requestedCursor,
			});
		} finally {
			state.isLoading = false;
			updateLoadMoreUi();
			logDebug(`request #${requestId} end`, {
				hasMore: state.hasMore,
				isLoading: state.isLoading,
				buttonHidden: loadMoreButton ? loadMoreButton.hidden : null,
				buttonDisplay: loadMoreButton ? loadMoreButton.style.display : null,
				endMessageHidden: endMessageEl ? endMessageEl.hidden : null,
			});
		}
	};

	updateLoadMoreUi();
	logDebug("init", {
		mode: activeMode,
		hasMore: state.hasMore,
		currentCategoryId: state.currentCategoryId,
		initialCursor: {
			categoryIndex: state.cursorCategoryIndex,
			jobOffset: state.cursorJobOffset,
		},
	});

	if (!state.hasMore) {
		return;
	}

	if (activeMode === LISTINGS_MODE_BUTTON) {
		if (loadMoreButton) {
			loadMoreButton.addEventListener("click", () => {
				fetchNextPage();
			});
		}

		return;
	}

	if (!("IntersectionObserver" in window)) {
		if (loadMoreButton) {
			loadMoreButton.hidden = !state.hasMore;
			loadMoreButton.addEventListener("click", () => {
				fetchNextPage();
			});
		}

		return;
	}

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					fetchNextPage();
				}
			});
		},
		{
			root: null,
			rootMargin: "250px 0px",
			threshold: 0,
		},
	);

	observer.observe(sentinel);
};

document.addEventListener("DOMContentLoaded", initInfiniteListings);
